/* eslint-disable radix, react/react-in-jsx-scope, default-case, no-new */
import Director from 'director';
import AppSelectionScreen, { AppSelectionBanner } from 'es6/components/apps/screens/app-selection';
import Config from 'es6/core/config';
import { noticeError } from 'es6/core/errors';
import Logger from 'es6/services/logger';
import * as Loaders from 'es6/core/loaders';
import * as Paths from 'es6/core/paths';
import IKnowApp from 'es6/controllers/apps/iknow-app';
import RapidChoiceApp from 'es6/controllers/apps/rapid-choice-app';
import SelfAssessmentApp from 'es6/controllers/apps/self-assessment-app';
import SentenceTrainerApp from 'es6/controllers/apps/sentence-trainer-app';
import SpecificContentApp from 'es6/controllers/apps/specific-content-app';
import ScreenBanner from 'es6/components/apps/screen-banner';

export default class AppLauncher {
  /**
   * Retrieves necessary study data from the URL and APIs. Then starts
   * an app or renders the app selection screen.
   *
   * @param {UserSettings} userSettings
   */
  constructor(userSettings, siteConfiguration) {
    this._cueLanguage = null;
    this._goalId = null;
    this._isStudyAll = false;
    this._userSettings = userSettings;
    this._siteConfiguration = siteConfiguration;

    this._screenManager = new Smartfm.Apps.ScreenManager();
    this._screenManager.addScreen('loading', Smartfm.Apps.Common.Screens.Loading);

    // Set up the router. Since the app uses replaceState, instead of pushState,
    // this is only used to set up initial information. Afterwards, the URL
    // is updated using the _replaceState() method.
    const router = new Director.Router().configure({
      html5history: true,
      run_handler_in_init: true,
    });

    // Set up potential parameters.
    router.param('goalId', /(\d+)/);
    router.param('language', /([A-Za-z-_]+)/);
    router.param('appPath', /([a-z_]+)/);

    // Set up the routes.
    router.on('/apps/courses/:goalId/:appPath', this._handleGoalRoute.bind(this));
    router.on('/apps/courses/:goalId', this._handleGoalRoute.bind(this));
    router.on('/apps/study_all/:language/:appPath', this._handleStudyAllRoute.bind(this));
    router.on('/apps/study_all/:language', this._handleStudyAllRoute.bind(this));

    // Specific content behaves a little differently, just launch the app
    // separately.
    router.on('/apps/specific_content', () => new SpecificContentApp({
      userSettings: this._userSettings,
    }));

    // Initial the router.
    router.init();
  }

  /**
   * Handles the routes for single goals.
   *
   * @param {String} goalId       this is a string because it is taken from the route
   * @param {String} appPath
   */
  _handleGoalRoute(goalId, appPath = null) {
    // Cast the goalId to a string
    this._goalId = parseInt(goalId);
    this._loadApp(appPath);
  }

  /**
   * Handles the routes for study all.
   *
   * @param {String} language
   * @param {String} appPath
   */
  _handleStudyAllRoute(language, appPath = null) {
    this._cueLanguage = language;
    this._isStudyAll = true;
    this._loadApp(appPath);
  }

  /**
   * Helper method for loading the app. If no app is specified, will
   * fallback on the load application screen.
   *
   * @param {String} appPath
   */
  _loadApp(appPath = null) {
    if (appPath) {
      this._loadAppData()
        .then(() => this._handleAppSelection(appPath))
        .catch(reason => this._onLoaderError(reason));
    } else {
      this._loadAppSelection();
    }
  }

  /**
   * Pre-emptively load app data, which can be used to acquire more
   * app settings and will make loading faster once an app is chosen.
   *
   * @returns {Promise}     containing appropriate data for the app screen
   */
  _loadAppData() {
    const goalId = this._goalId;

    if (goalId) {
      return Promise.all([
        new Loaders.GoalsLoader([goalId]),
        new Loaders.AggregateMemoryLoader(goalId),
      ]).then(responses => {
        const goal = responses[0][0];
        const aggregateMemory = responses[1];

        // Set cue language for app.
        this._cueLanguage = goal.cueLanguage;

        return { goal, aggregateMemory };
      });
    } else {
      return Promise.resolve({});
    }
  }

  /**
   * Callback for when the loader fails.
   *
   * @param {Error} reason
   */
  _onLoaderError(reason) {
    noticeError(reason);
    Logger.error('AppLauncher', reason, reason.stack);
  }

  /**
   * Shows the app selection screen. Will grab session data to
   * show things like the goal and memory statistics.
   */
  _loadAppSelection() {
    this._screenManager.show('loading');

    this._loadAppData().then(data => {
      this.show(data.goal, data.aggregateMemory);
    }).catch(this._onLoaderError);
  }

  /**
   * Renders the AppSelectionScreen screen.
   *
   * @params {Goal} goal
   * @params {AggregateMemory} aggregateMemory
   */
  show(goal, aggregateMemory) {
    this._screenManager.renderComponent(
      AppSelectionScreen,
      {
        aggregateMemory,
        goal,
        onAppSelection: (appPath) => this._handleAppSelection(appPath),
        isStudyAll: !goal,
      }
    );

    // Also render the banner for the screen.
    this._screenManager.renderBannerComponent(
      ScreenBanner,
      {
        showPauseButton: false,
        showSettingsButton: false,
        children: <AppSelectionBanner goal={goal} />,
      }
    );
  }

  /**
   * Callback for when an app is chosen on app selection screen.
   *
   * @param {String} appPath
   * @param {Boolean} isCheckpoint     will pass down isCheckpoint variable
   */
  _handleAppSelection(appPath, isCheckpoint = false) {
    const appSettings = {
      cueLanguage: this._cueLanguage,
      goalId: this._goalId,
      isCheckpoint,
      onAppQuit: this._handleAppQuit.bind(this),
      userSettings: this._userSettings,
      siteConfiguration: this._siteConfiguration,
    };

    // Pull in any SmartFM settings before showing the app screen.
    Config.refreshSettings();

    // Show the loading screen.
    this._screenManager.show('loading');

    // Reset the banneer.
    this._screenManager.renderBannerComponent(
      ScreenBanner,
      {
        showPauseButton: false,
        showSettingsButton: false,
      }
    );

    switch (appPath) {
      case Paths.AppPaths.IKNOW:
        new IKnowApp(appSettings);
        break;
      case Paths.AppPaths.RAPID_CHOICE:
        new RapidChoiceApp(appSettings);
        break;
      case Paths.AppPaths.SELF_ASSESSMENT:
        new SelfAssessmentApp(appSettings);
        break;
      case Paths.AppPaths.SENTENCE_TRAINER:
        new SentenceTrainerApp(appSettings);
        break;
    }

    this._replaceState(appPath);
  }

  /**
   * Quits an app and shows the app selection screen.
   *
   * @param {String}  appPath        open a new app
   * @param {Boolean} isCheckpoint   if new app is a checkpoint
   */
  _handleAppQuit(appPath = null, isCheckpoint = false) {
    if (appPath) {
      this._handleAppSelection(appPath, isCheckpoint);
    } else {
      this._replaceState();
      this._loadAppSelection();
    }
  }

  /**
   * Replaces the history state with the current path.
   *
   * @param {String} appPath
   */
  _replaceState(appPath = null) {
    // For browsers that don't support pushstate, just ignore and
    // don't do anything.
    if (!window.history || !window.history.replaceState) {
      return;
    }

    let newPath;

    if (this._isStudyAll) {
      newPath = Paths.studyAll(this._cueLanguage, appPath);
    } else {
      newPath = Paths.studyGoal(this._goalId, appPath);
    }

    newPath += window.location.search;

    window.history.replaceState({}, document.title, newPath);
  }
}
